import { useEffect, useState } from 'react';
import { randomId } from '../lib/id';

/**
 * Tabs of the same browser share one session cookie but otherwise know nothing of each
 * other. They talk over a BroadcastChannel: a sign-in, sign-out or profile change in one
 * tab is announced so the others follow at once, and tabs answer a "who is open?" ping.
 * Browsers without BroadcastChannel simply keep the old behaviour.
 */
const CHANNEL = 'pd-auth';

export type TabMessage =
  /** The signed-in user changed (null = signed out) or their profile was updated. */
  | { type: 'auth'; userId: string | null }
  | { type: 'ping'; from: string }
  | { type: 'pong'; from: string; to: string };

export const TAB_ID = randomId();

export function openChannel(onMessage?: (msg: TabMessage) => void): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  const channel = new BroadcastChannel(CHANNEL);
  if (onMessage) channel.onmessage = (e: MessageEvent<TabMessage>) => onMessage(e.data);
  return channel;
}

/** How many tabs of this browser have the platform open (this one included). */
export function useOpenTabs(refreshMs = 5000): number {
  const [count, setCount] = useState(1);
  useEffect(() => {
    let seen = new Set<string>();
    const channel = openChannel((msg) => {
      if (msg.type === 'pong' && msg.to === TAB_ID && msg.from !== TAB_ID) seen.add(msg.from);
    });
    if (!channel) return;
    let settle: ReturnType<typeof setTimeout>;
    const ask = () => {
      seen = new Set();
      channel.postMessage({ type: 'ping', from: TAB_ID } satisfies TabMessage);
      clearTimeout(settle);
      settle = setTimeout(() => setCount(1 + seen.size), 300);
    };
    ask();
    const timer = setInterval(ask, refreshMs);
    return () => {
      clearInterval(timer);
      clearTimeout(settle);
      channel.close();
    };
  }, [refreshMs]);
  return count;
}
